import queryRef from './query';
import { UserNotFoundError } from 'utils/errors/firebaseError';
import { handleError } from 'utils/errorHandler';
import { GeoFirestore } from './config';

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} emailAddress
 * @property {string} company
 * @property {string} phoneNumber
 * @property {string[]} address
 * @property {boolean} status
 * @property {boolean} isSuperAdmin
 * @property {number} noOfExperience
 * @property {number} createdAt
 *
 */

/**
 * Get user by UUID
 * @param {string} uuid user UUID
 */

export const getUserById = uuid => {
  const userRef = queryRef.ADMIN_USER.doc(uuid);
  return userRef.get();
};

/**
 * Check if user exist or not in adminUser Collection.
 *
 * @param {string} id UUID.
 */
export const checkIfUserExist = async id => {
  try {
    const doc = await getUserById(id);
    if (!doc.exists) {
      throw new UserNotFoundError();
    }
    return true;
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 *
 * @param {User} user(Admin)
 * @returns {Promise}
 */
export const createUser = user => {
  return queryRef.ADMIN_USER.doc(user.id).set(user);
};

export const getClientList = filterBy => {
  if (filterBy !== 'all') {
    return queryRef.ADMIN_USER.where('isSuperAdmin', '==', false)
      .where('status', '==', filterBy)
      .orderBy('createdAt', 'desc')
      .get();
  }
  return queryRef.ADMIN_USER.where('isSuperAdmin', '==', false).orderBy('createdAt', 'desc').get();
};

/**
 * Check if emailaddress exist or not in adminUser Collection
 * @param {string} emailAddress
 */
export const checkIfEmailAddressExist = async emailAddress => {
  try {
    const doc = await queryRef.ADMIN_USER.where('emailAddress', '==', emailAddress).get();
    if (!doc.empty) {
      return true;
    }
    return false;
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Check if emailaddress exist or not in appUsers Collection
 * @param {string} emailAddress
 */
export const checkIfEmailAddressExistInApp = async emailAddress => {
  try {
    const doc = await queryRef.APP_USERS.where('email', '==', emailAddress).get();
    if (!doc.empty) {
      return true;
    }
    return false;
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Update the client profile info
 * @param {object} clientInfo Updated Client Info
 * @param {string} uuid
 */
export const updateClientInfo = (clientInfo, uuid) => {
  try {
    const a = queryRef.ADMIN_USER.doc(uuid).update(clientInfo);
    return a;
  } catch (err) {
    handleError(err);
  }
};
// export const updateClientInfo = (clientInfo, uuid) => {
//   return queryRef.ADMIN_USER.doc(uuid).update(clientInfo);
// };

/**
 * Get User by email address
 * @param {string} emailAddress
 */
export const getUserByEmail = async emailAddress => {
  try {
    const doc = await queryRef.ADMIN_USER.where('emailAddress', '==', emailAddress).get();
    if (doc.empty) {
      const error = new Error('User Doesn\'t Exist'); //prettier-ignore
      error.code = 'auth/user-not-found';
      throw error;
    }
    return doc.docs[0].data();
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Suspend User
 * @param {string} userID
 * @param {('active'| 'invited'| 'suspended')} status
 */
export const updateUserStatus = (userID, status) => {
  const adminUserRef = queryRef.USER_INFO_BY_ID(userID);
  return adminUserRef.update({ status: status });
};

export const getInvitedUserById = async uuid => {
  const userRef = queryRef.INVITED_CLIENTS.doc(uuid);
  return userRef.get();
};

export const getActiveUserList = async () => {
  try {
    const adminUserCollection = await queryRef.ADMIN_USER.where('status', '==', 'active').get();
    const activeUserList = adminUserCollection.docs.map(doc => {
      return {
        value: doc.data().id,
        label: doc.data().fullName,
        description: doc.data().emailAddress,
        companyName: doc.data().company,
      };
    });
    return activeUserList;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getAllUserList = async () => {
  try {
    const userCollection = await queryRef.APP_USERS.get();

    const userList = userCollection.docs.map(doc => {
      return {
        id: doc.data().id,
        label: doc.data().name,
        discountStatus: doc.data().discountStatus,
        active: doc.data().discountStatus?.usedAt,
      };
    });
    return userList;
  } catch (error) {
    return Promise.reject(error);
  }
};

export function setGlobalDiscountCouponDetails(
  appUserId,
  discountCode,
  discountPercentage,
  createdBy,
  startDate,
  endDate,
  event
) {
  const ref = GeoFirestore.collection('discountCoupon');
  const refUser = GeoFirestore.collection('appUsers');

  const docID = ref.doc().id;

  const document = {
    id: docID,
    appUserId,
    discountCode,
    discountPercentage,
    createdBy,
    createdAt: Date.now(),
    event,
    startDate,
    endDate,
    isUsed: false,
    isActive: true,
  };

  return ref
    .doc(docID)
    .set(
      {
        ...document,
      },
      { merge: true }
    )
    .then(() => {
      refUser.doc(appUserId).update({
        discountStatus: {
          discountCode: discountCode,
          createdAt: document.createdAt,
          discountPercentage: discountPercentage,
          event,
        },
      });
    })
    .then(() => {
      return { docID };
    });
}
